/**
 * Library Collections, Phase A contract (frozen in AB1).
 *
 * A collection holds Resources (documents, datasets, studies, the org's own
 * audiences) and Topics (social listening). Membership is hybrid: rule
 * matches plus manual includes, minus excludes. No row ids anywhere: every
 * item and every facet rule is addressed by its public token.
 */

export const LIBRARY_COLLECTION_PRESETS = [
  'brand',
  'competition',
  'trend',
  'research',
  'custom',
] as const;
export type LibraryCollectionPreset =
  (typeof LIBRARY_COLLECTION_PRESETS)[number];

export const LIBRARY_RESOURCE_KINDS = [
  'document',
  'dataset',
  'audience',
  'study',
] as const;
export type LibraryResourceKind = (typeof LIBRARY_RESOURCE_KINDS)[number];

export const LIBRARY_ITEM_KINDS = [...LIBRARY_RESOURCE_KINDS, 'topic'] as const;
export type LibraryItemKind = (typeof LIBRARY_ITEM_KINDS)[number];

/**
 * Kinds a member row stores (D6). `dataset` is never stored: it is derived
 * from the document on read, so a reclassified document moves between
 * Datasets and Documents with no write. Mirrors the table's `check()`.
 */
export const LIBRARY_MEMBER_STORED_KINDS = [
  'document',
  'audience',
  'study',
  'topic',
] as const;
export type LibraryMemberStoredKind =
  (typeof LIBRARY_MEMBER_STORED_KINDS)[number];

export const LIBRARY_MEMBER_MODES = ['include', 'exclude'] as const;
export type LibraryMemberMode = (typeof LIBRARY_MEMBER_MODES)[number];

export const LIBRARY_MEMBERSHIPS = ['rule', 'manual'] as const;
export type LibraryMembership = (typeof LIBRARY_MEMBERSHIPS)[number];

export const LIBRARY_KPI_WINDOWS = ['30d', '90d', 'all'] as const;
export type LibraryKpiWindow = (typeof LIBRARY_KPI_WINDOWS)[number];

/** Rule fields that can match (D1a). `version` and `kinds` are not among them. */
export const LIBRARY_RULE_FIELDS = [
  'brands',
  'markets',
  'categories',
  'methods',
  'years',
  'topicBrands',
] as const;
export type LibraryRuleField = (typeof LIBRARY_RULE_FIELDS)[number];

/** Facet-token rule fields, each with the facet kind its tokens must have. */
export const LIBRARY_FACET_RULE_FIELD_KINDS = {
  brands: 'brand',
  markets: 'market',
  categories: 'category',
  methods: 'method',
  years: 'year',
} as const;
export type LibraryFacetRuleField = keyof typeof LIBRARY_FACET_RULE_FIELD_KINDS;

/** A counted document facet (D1): an auto tag counts at this confidence or above. */
export const LIBRARY_COUNTED_FACET_CONFIDENCE = 0.75;

/** The fields of a document facet assignment the counted rule reads. */
export interface LibraryCountedFacetFields {
  provenance: 'auto' | 'human' | 'connector';
  confirmed: boolean;
  rejected?: boolean;
  confidence?: number | null;
}

/**
 * D1 and D5, the client mirror of `countedFacetPredicate` (backend
 * `rules-sql.ts`): not rejected, and human, connector, confirmed, at or above
 * the threshold, or an auto tag not scored yet. The Library filter uses it so
 * a filtered view and the saved filter built from it hold the same
 * documents. Pinned against the SQL by `knowledge-facets-aggregate.postgres.spec.ts`.
 */
export function isCountedFacetAssignment(facet: LibraryCountedFacetFields): boolean {
  if (facet.rejected) {
    return false;
  }
  if (facet.provenance === 'human' || facet.provenance === 'connector' || facet.confirmed) {
    return true;
  }
  const confidence = facet.confidence ?? null;
  if (confidence === null) {
    return facet.provenance === 'auto';
  }
  return confidence >= LIBRARY_COUNTED_FACET_CONFIDENCE;
}

export interface LibraryTopicBrandRule {
  topicToken: string;
  brandId: string;
}

/**
 * Rule semantics (D1a): OR within a field, AND across fields. No facet field
 * means no rule matches. `kinds` only narrows.
 */
export interface LibraryCollectionRules {
  version: 1;
  /** Facet value tokens. */
  brands?: Array<string>;
  markets?: Array<string>;
  categories?: Array<string>;
  methods?: Array<string>;
  years?: Array<string>;
  kinds?: Array<LibraryResourceKind>;
  topicBrands?: Array<LibraryTopicBrandRule>;
}

/**
 * Who can see a collection (D2). Same values as the platform `SharingMode`
 * (`components/sharing/ShareSettingsTypes.ts`): `private` = only the owner,
 * `shared` = the owner and the collaborators, `organization` = the whole
 * org. Guests and public links are not offered for collections.
 */
export const LIBRARY_COLLECTION_SHARING_MODES = [
  'private',
  'shared',
  'organization',
] as const;
export type LibraryCollectionSharingMode =
  (typeof LIBRARY_COLLECTION_SHARING_MODES)[number];

/** New collections and saved filters default to the whole org (D2). */
export const DEFAULT_LIBRARY_COLLECTION_SHARING_MODE: LibraryCollectionSharingMode =
  'organization';

/**
 * D1: `curated` = picked by a person (rules and members); `saved_filter` =
 * a rules-only collection saved from the Library filters.
 */
export const LIBRARY_COLLECTION_ORIGINS = ['curated', 'saved_filter'] as const;
export type LibraryCollectionOrigin =
  (typeof LIBRARY_COLLECTION_ORIGINS)[number];

/** The one collaborator role collections offer. */
export const LIBRARY_COLLECTION_COLLABORATOR_ROLES = ['collaborator'] as const;
export type LibraryCollectionCollaboratorRole =
  (typeof LIBRARY_COLLECTION_COLLABORATOR_ROLES)[number];

export interface LibraryCollectionCover {
  kind: LibraryItemKind;
  imageUrl: string | null;
  title: string;
}

export interface LibraryCollectionCard {
  token: string;
  name: string;
  preset: LibraryCollectionPreset;
  /** Viewer-visible; audiences are org-owned only. */
  resourceCounts: Record<LibraryResourceKind, number>;
  topicCount: number;
  twinsCount: number;
  cover: Array<LibraryCollectionCover>;
  updatedAt: string;
  canEdit: boolean;
  sharingMode: LibraryCollectionSharingMode;
  origin: LibraryCollectionOrigin;
  /** The viewer created it; only the owner manages sharing. */
  isOwner: boolean;
}

/** The list is paged (D9): 50 per page by `updated_at desc, token`. */
export interface LibraryCollectionCardsPage {
  items: Array<LibraryCollectionCard>;
  nextCursor: string | null;
}

export interface LibraryItemFacet {
  kind: string;
  value: string;
  token: string;
}

export interface LibraryAudienceSummary {
  countryCodes?: Array<string>;
  twinsCount: number;
  audienceType: 'B2B' | 'B2C' | null;
  uploaded: boolean;
}

export interface LibraryCollectionItem {
  kind: LibraryItemKind;
  token: string;
  title: string;
  /** The item's summary first, then its kind-specific fallback (description, link, objective). */
  subtitle: string | null;
  occurredAt: string;
  imageUrl: string | null;
  /** Up to four twin portraits for an audience or a study (its audience's twins); absent for other kinds. */
  portraits?: Array<string>;
  membership: LibraryMembership;
  facets: Array<LibraryItemFacet>;
  audience?: LibraryAudienceSummary;
}

export interface LibraryCollectionResourcesPage {
  items: Array<LibraryCollectionItem>;
  nextCursor: string | null;
}

export interface LibrarySearchTheme {
  token: string;
  name: string;
  posts: number;
  topicCount: number;
}

export interface LibrarySearchTopic {
  token: string;
  name: string;
  collectionCount: number;
}

export interface LibrarySearchCollection {
  token: string;
  name: string;
  preset: LibraryCollectionPreset;
  sharingMode: LibraryCollectionSharingMode;
  origin: LibraryCollectionOrigin;
}

export interface LibrarySearchBrand {
  name: string;
  /**
   * Every source of this brand name, in two forms: a brand facet value
   * token (a UUID, for `rules.brands`) or a Topic brand pair written
   * `${topicToken}:${brandId}` (for `rules.topicBrands`, split on the colon).
   */
  brandTokens: Array<string>;
  collectionToken: string | null;
}

export interface LibrarySearchResponse {
  /** `[]` until Phase B. */
  themes: Array<LibrarySearchTheme>;
  topics: Array<LibrarySearchTopic>;
  collections: Array<LibrarySearchCollection>;
  brands: Array<LibrarySearchBrand>;
  resources: Array<LibraryCollectionItem>;
}

/** "See all" for one search group. */
export const LIBRARY_SEARCH_GROUPS = [
  'themes',
  'topics',
  'collections',
  'brands',
  'resources',
] as const;
export type LibrarySearchGroup = (typeof LIBRARY_SEARCH_GROUPS)[number];

export interface LibrarySearchGroupPage<T> {
  group: LibrarySearchGroup;
  items: Array<T>;
  nextCursor: string | null;
}

/** Typed KPI keys per preset (D10). `custom` uses the research set. */
export const LIBRARY_KPI_KEYS = {
  brand: ['mentions', 'brandNetSentiment', 'studies', 'documents'],
  competition: ['shareOfVoice', 'netSentimentGap', 'studies', 'documents'],
  trend: ['postsInWindow', 'studies', 'audiences', 'documents'],
  research: ['studies', 'datasets', 'audiences', 'twins'],
} as const;
export type LibraryKpiKey =
  (typeof LIBRARY_KPI_KEYS)[keyof typeof LIBRARY_KPI_KEYS][number];

export const LIBRARY_KPI_UNITS = ['count', 'percent', 'net'] as const;
export type LibraryKpiUnit = (typeof LIBRARY_KPI_UNITS)[number];

export interface LibraryKpiCallout {
  key: LibraryKpiKey;
  value: number | null;
  delta?: number | null;
  unit: LibraryKpiUnit;
}

export type LibraryKpiCallouts = [
  LibraryKpiCallout,
  LibraryKpiCallout,
  LibraryKpiCallout,
  LibraryKpiCallout,
];

/** Discriminated by preset (D10); `custom` gets the research set. */
export type LibraryCollectionKpis =
  | { preset: 'brand'; window: LibraryKpiWindow; callouts: LibraryKpiCallouts }
  | {
      preset: 'competition';
      window: LibraryKpiWindow;
      callouts: LibraryKpiCallouts;
    }
  | { preset: 'trend'; window: LibraryKpiWindow; callouts: LibraryKpiCallouts }
  | {
      preset: 'research' | 'custom';
      window: LibraryKpiWindow;
      callouts: LibraryKpiCallouts;
    };

/** The KPI key set a preset's callouts carry, in order. */
export function libraryKpiKeysForPreset(
  preset: LibraryCollectionPreset,
): ReadonlyArray<LibraryKpiKey> {
  if (preset === 'custom') {
    return LIBRARY_KPI_KEYS.research;
  }
  return LIBRARY_KPI_KEYS[preset];
}

export interface LibraryRuleLabel {
  field: LibraryRuleField;
  /** For `topicBrands`, `${topicToken}:${brandId}`. */
  token: string;
  label: string;
}

export interface LibraryCollectionCreator {
  name: string;
  initials: string;
}

export interface LibraryCollectionDetail {
  token: string;
  name: string;
  description: string | null;
  preset: LibraryCollectionPreset;
  rules: LibraryCollectionRules;
  ruleLabels: Array<LibraryRuleLabel>;
  resourceCounts: Record<LibraryResourceKind, number>;
  topicCount: number;
  twinsCount: number;
  createdBy: LibraryCollectionCreator | null;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  sharingMode: LibraryCollectionSharingMode;
  origin: LibraryCollectionOrigin;
  /** The viewer created it; only the owner manages sharing. */
  isOwner: boolean;
  /** Same as the card's: up to 4 viewer-visible members, items with an image first. */
  cover: Array<LibraryCollectionCover>;
}

/** The add picker's candidates, already filtered by D7, D8 (org scope) and D15. */
export interface LibraryCandidatesQuery {
  kind: LibraryItemKind;
  q?: string;
  cursor?: string;
}
// response: LibraryCollectionResourcesPage (topics come back as items with kind 'topic')

export interface LibraryItemRef {
  /** `dataset` is accepted as `document`. */
  kind: LibraryItemKind;
  token: string;
}

export interface CreateLibraryCollectionBody {
  name: string;
  description?: string | null;
  preset: LibraryCollectionPreset;
  rules: LibraryCollectionRules;
  includes?: Array<LibraryItemRef>;
  /** Defaults to `organization`. */
  sharingMode?: LibraryCollectionSharingMode;
  /** Defaults to `curated`. */
  origin?: LibraryCollectionOrigin;
}

/** All optional. `sharingMode` is owner only. */
export interface UpdateLibraryCollectionBody {
  name?: string;
  description?: string | null;
  preset?: LibraryCollectionPreset;
  rules?: LibraryCollectionRules;
  sharingMode?: LibraryCollectionSharingMode;
  origin?: LibraryCollectionOrigin;
}

/**
 * Collaborator API, shaped like the audience share API
 * (`/populations/:id/sharing*`), owner only:
 *
 * - `GET    /library/collections/:token/sharing` returns `LibraryCollectionSharing`
 * - `PATCH  /library/collections/:token/sharing` takes `UpdateLibraryCollectionSharingBody`, returns `{ sharingMode }`
 * - `POST   /library/collections/:token/sharing/collaborators` takes `AddLibraryCollectionCollaboratorBody`, returns `LibraryCollectionCollaborator`
 * - `DELETE /library/collections/:token/sharing/collaborators/:collaboratorId` returns `{ success: true }`
 *
 * `id` is the collaborator row token and `userId` the user token, never row ids.
 */
export interface LibraryCollectionCollaborator {
  id: string;
  userId: string;
  role: LibraryCollectionCollaboratorRole;
  name: string | null;
  email: string | null;
  createdAt: string;
}

export interface LibraryCollectionSharing {
  sharingMode: LibraryCollectionSharingMode;
  collaborators: Array<LibraryCollectionCollaborator>;
}

export interface UpdateLibraryCollectionSharingBody {
  sharingMode: LibraryCollectionSharingMode;
}

export interface AddLibraryCollectionCollaboratorBody {
  /** The org member's user token. */
  userId: string;
}

export interface RemoveLibraryCollectionCollaboratorResponse {
  success: true;
}

export interface LibraryCollectionMemberBody {
  kind: LibraryItemKind;
  token: string;
  mode: LibraryMemberMode;
}

export interface LibraryCollectionTopicBrand {
  id: string;
  name: string;
}

export interface LibraryCollectionTopicItem {
  token: string;
  name: string;
  /** `rule` = linked by `topicBrands`. */
  membership: LibraryMembership;
  platforms: Array<string>;
  posts: number;
  lastSyncedAt: string | null;
  brands: Array<LibraryCollectionTopicBrand>;
  collectionCount: number;
}

export interface LibraryCollectionTopicsResponse {
  items: Array<LibraryCollectionTopicItem>;
  socialListeningEnabled: boolean;
}

export interface LibraryCollectionPreviewBody {
  preset: LibraryCollectionPreset;
  rules: LibraryCollectionRules;
  includes?: Array<LibraryItemRef>;
}

export interface LibraryCollectionPreviewResponse {
  resourceCounts: Record<LibraryResourceKind, number>;
  topicCount: number;
  twinsCount: number;
  /** At most 6, for the dialog. */
  sample: Array<LibraryCollectionItem>;
}

export interface LibraryItemCollection {
  token: string;
  name: string;
  preset: LibraryCollectionPreset;
  membership: LibraryMembership;
  canEdit: boolean;
  sharingMode: LibraryCollectionSharingMode;
  origin: LibraryCollectionOrigin;
}

export interface LibraryItemCollectionsResponse {
  collections: Array<LibraryItemCollection>;
}

/** D1: a saved filter holds rules only, so only a curated collection the viewer can edit takes members. */
export function canAddLibraryCollectionMembers(
  collection: Pick<LibraryCollectionCard, 'canEdit' | 'origin'>,
): boolean {
  return collection.canEdit && collection.origin !== 'saved_filter';
}

/** Maps a route or body kind to the stored kind (`dataset` is a document). */
export function toLibraryMemberStoredKind(
  kind: LibraryItemKind,
): LibraryMemberStoredKind {
  if (kind === 'dataset') {
    return 'document';
  }
  return kind;
}
